// client/src/components/pages/LexiStar/RecomputeJobs/DiffMap.jsx
//
// The diff's map toggle (LS-09 §5.19): arrivals and departures a recompute
// moved, each as a hollow ring where it was, a filled dot where it is now and a
// thin line between them — before/after differ by fill, not by colour. The
// same points are listed under the map, labelled arrival or departure, with
// their old and new times.

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Source, Layer } from 'react-map-gl/maplibre';
import LexiStarMap from '../../../map/LexiStarMap';
import { boundsFromLngLats } from '../../../map/geo';
import { fmtDateTime } from '../StatusBoard/format';
import { movedPoints } from './recomputeLogic';

const INK = '#0f172a';
const DEFAULT_VIEW = { longitude: 28.5, latitude: -26.2, zoom: 6 };

export default function DiffMap({ rows }) {
  const points = useMemo(() => movedPoints(rows), [rows]);
  const mapRef = useRef(null);

  const fc = useMemo(() => {
    const features = [];
    points.forEach((p) => {
      if (p.before && p.after)
        features.push({
          type: 'Feature',
          properties: { role: 'link' },
          geometry: { type: 'LineString', coordinates: [p.before, p.after] },
        });
      if (p.before)
        features.push({
          type: 'Feature',
          properties: { role: 'before', kind: p.kind },
          geometry: { type: 'Point', coordinates: p.before },
        });
      if (p.after)
        features.push({
          type: 'Feature',
          properties: { role: 'after', kind: p.kind },
          geometry: { type: 'Point', coordinates: p.after },
        });
    });
    return { type: 'FeatureCollection', features };
  }, [points]);

  const bounds = useMemo(
    () => boundsFromLngLats(points.flatMap((p) => [p.before, p.after].filter(Boolean))),
    [points]
  );
  const fit = useCallback(() => {
    if (!mapRef.current || !bounds) return;
    try {
      mapRef.current.fitBounds(bounds, { padding: 48, duration: 0, maxZoom: 16 });
    } catch {
      /* not ready */
    }
  }, [bounds]);
  useEffect(() => {
    fit();
  }, [fit]);

  if (!points.length) {
    return (
      <p className="text-sm text-slate-500 py-6 text-center">
        No arrivals or departures with positions moved on this page of the diff.
      </p>
    );
  }

  return (
    <div>
      <div className="relative z-0 h-[360px] rounded-lg overflow-hidden border border-slate-200">
        <LexiStarMap
          className="absolute inset-0"
          initialViewState={DEFAULT_VIEW}
          defaultBasemap="street"
          navigationPosition="top-left"
          onLoad={(e) => {
            mapRef.current = e.target;
            fit();
          }}
        >
          <Source id="diff-moves" type="geojson" data={fc}>
            <Layer
              id="diff-moves-link"
              type="line"
              filter={['==', ['get', 'role'], 'link']}
              paint={{ 'line-color': INK, 'line-width': 1.5, 'line-dasharray': [2, 1.5] }}
            />
            <Layer
              id="diff-moves-before"
              type="circle"
              filter={['==', ['get', 'role'], 'before']}
              paint={{
                'circle-radius': 6,
                'circle-color': '#ffffff',
                'circle-opacity': 0.9,
                'circle-stroke-color': INK,
                'circle-stroke-width': 2,
              }}
            />
            <Layer
              id="diff-moves-after"
              type="circle"
              filter={['==', ['get', 'role'], 'after']}
              paint={{
                'circle-radius': 5,
                'circle-color': INK,
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 1.5,
              }}
            />
          </Source>
        </LexiStarMap>
      </div>
      <ul
        className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-slate-500"
        aria-label="Diff map legend"
      >
        <li className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-full border-2 border-slate-900 bg-white" />{' '}
          Before
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-full bg-slate-900" /> After
        </li>
      </ul>
      <table className="w-full mt-3 text-sm" aria-label="Moved arrivals and departures">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
            <th className="py-1 pr-3 font-semibold">Moved</th>
            <th className="py-1 pr-3 font-semibold">Before</th>
            <th className="py-1 font-semibold">After</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {points.map((p) => (
            <tr key={p.id}>
              <td className="py-1 pr-3 text-slate-800">
                {p.kind === 'arrival' ? 'Arrival' : 'Departure'}{' '}
                <span className="font-mono text-xs text-slate-500">{p.type}</span>
              </td>
              <td className="py-1 pr-3 tabular-nums text-slate-500">{fmtDateTime(p.beforeAt)}</td>
              <td className="py-1 tabular-nums text-slate-900 font-medium">
                {fmtDateTime(p.afterAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
